<script>
    import { createEventDispatcher } from "svelte";
    import { fade } from "svelte/transition";
    import axios from "../../utiles/axios.util";

    // 팝업 표시 여부
    export let isOpen = false;
    // USP_MNGMT_DTL 조회 조건 (plan_week_no, period_se, year, period, mlsfc_code, item_group_code, ...)
    export let params = null;
    // 헤더 요약 표시용 (planWeek: 편성주차, periodLabel: 더블클릭한 열 이름, filters: [{ label, value }])
    export let meta = { planWeek: "", periodLabel: "", filters: [] };

    const dispatch = createEventDispatcher();

    // 수주 상세 컬럼 (USP_MNGMT_DTL 결과 컬럼)
    const COLUMNS = [
        { key: "so_no", label: "수주번호", align: "center", mono: true, width: 100 },
        { key: "so_type_nm", label: "수주구분", align: "center", badge: true, width: 88 },
        { key: "dmnd_nm", label: "수요처", align: "left", wrap: true, width: 170 },
        { key: "item_cd", label: "품번", align: "center", mono: true, width: 110 },
        { key: "mhrls_knd_code", label: "기종", align: "center", width: 80 },
        { key: "so_qty", label: "수주수량", align: "right", num: true, width: 74 },
        { key: "gi_qty", label: "출고수량", align: "right", num: true, width: 74 },
        { key: "bill_qty", label: "판매수량", align: "right", num: true, width: 74 },
        { key: "dlvy_dt", label: "납기일자", align: "center", mono: true, width: 96 },
        { key: "promise_dt", label: "출하의뢰일자", align: "center", mono: true, width: 104 },
        { key: "cell_qty", label: "집계수량", align: "right", num: true, agg: true, width: 80 },
    ];

    let rows = [];
    let isLoading = false;
    let errorMsg = "";
    let fetchToken = 0;

    // 팝업이 열려 있는 동안 조회 조건이 바뀌면 다시 조회
    $: if (isOpen && params) {
        load(params);
    }
    $: if (!isOpen) {
        // 닫히면 다음 오픈 시 이전 데이터가 깜빡이지 않도록 초기화 (위치도 정중앙으로 되돌림)
        pos = null;
        dragging = false;
        rows = [];
        errorMsg = "";
        isLoading = false;
        fetchToken++;
    }

    async function load(p) {
        const token = ++fetchToken;
        isLoading = true;
        errorMsg = "";
        rows = [];
        try {
            const res = await axios.get("bsnMngmt/orderDetail", { params: p });
            if (token !== fetchToken) return;
            if (res && res.data && res.data.code === 200) {
                rows = Array.isArray(res.data.data) ? res.data.data : [];
            } else {
                errorMsg = (res && res.data && res.data.msg) || "수주 상세 조회에 실패했습니다.";
            }
        } catch (e) {
            if (token !== fetchToken) return;
            errorMsg = (e && e.response && e.response.data && e.response.data.msg) || e.message || "수주 상세 조회 중 오류가 발생했습니다.";
        } finally {
            if (token === fetchToken) isLoading = false;
        }
    }

    $: totals = rows.reduce(
        (acc, r) => {
            acc.so_qty += Number(r.so_qty) || 0;
            acc.gi_qty += Number(r.gi_qty) || 0;
            acc.bill_qty += Number(r.bill_qty) || 0;
            acc.cell_qty += Number(r.cell_qty) || 0;
            return acc;
        },
        { so_qty: 0, gi_qty: 0, bill_qty: 0, cell_qty: 0 }
    );

    function formatQty(v) {
        const n = Number(v);
        if (!n) return "-";
        return n.toLocaleString();
    }

    function formatCell(col, row) {
        const v = row[col.key];
        if (col.num) return formatQty(v);
        if (v === null || v === undefined || String(v).trim() === "") return "-";
        return String(v).trim();
    }

    function handleClose() {
        dispatch("close");
    }

    // ──────────────────────────────────────────────────────────────
    // 창 이동 (헤더 드래그, 마우스/터치 공통). 뒷배경은 막지 않으며 X / 닫기 버튼으로만 닫힌다.
    // ──────────────────────────────────────────────────────────────
    let winEl;
    // 창 좌상단 좌표. null이면 화면 정중앙(CSS 중앙 정렬)이고, 처음 드래그하는 순간부터 좌표 방식으로 전환된다.
    // 팝업을 닫으면 null로 초기화되어 다음에 열 때도 정중앙에서 시작한다.
    let pos = null;
    let dragging = false;
    let dragOffset = { x: 0, y: 0 };

    // 헤더가 항상 화면 안에 남도록 좌표 보정
    function clampPos(x, y) {
        const w = winEl ? winEl.getBoundingClientRect().width : 400;
        return {
            x: Math.min(Math.max(x, 120 - w), window.innerWidth - 120),
            y: Math.min(Math.max(y, 0), window.innerHeight - 44),
        };
    }

    function onDragStart(e) {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.target.closest(".od-close")) return;
        if (!pos) {
            // 중앙 정렬 상태에서 처음 잡으면 현재 화면상 위치를 좌표로 고정
            const r = winEl.getBoundingClientRect();
            pos = { x: r.left, y: r.top };
        }
        dragging = true;
        dragOffset = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();
    }

    function onDragMove(e) {
        if (!dragging) return;
        pos = clampPos(e.clientX - dragOffset.x, e.clientY - dragOffset.y);
    }

    function onDragEnd(e) {
        if (!dragging) return;
        dragging = false;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* 무시 */ }
    }

    function onWindowResize() {
        if (isOpen && pos) pos = clampPos(pos.x, pos.y);
    }
</script>

<svelte:window on:resize={onWindowResize} />

{#if isOpen}
    <div class="od-layer" transition:fade={{ duration: 160 }}>
        <div
            class="od-box"
            class:is-dragging={dragging}
            bind:this={winEl}
            style={pos
                ? `left: ${pos.x}px; top: ${pos.y}px; max-height: calc(100vh - ${pos.y + 8}px);`
                : "left: 50%; top: 50%; transform: translate(-50%, -50%); max-height: calc(100vh - 32px);"}
        >
            <!-- 헤더 (드래그하여 창 이동) -->
            <div
                class="od-header"
                title="드래그하여 이동"
                on:pointerdown={onDragStart}
                on:pointermove={onDragMove}
                on:pointerup={onDragEnd}
                on:pointercancel={onDragEnd}
            >
                <div class="od-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="8" y1="13" x2="16" y2="13" />
                        <line x1="8" y1="17" x2="16" y2="17" />
                    </svg>
                    <h3>수주 상세</h3>
                    {#if !isLoading && !errorMsg}
                        <span class="od-count">총 {rows.length}건</span>
                    {/if}
                </div>
                <button type="button" class="od-close" on:click={handleClose} title="닫기">✕</button>
            </div>

            <!-- 조회 조건 요약 -->
            <div class="od-summary">
                <div class="od-chip od-chip-primary">
                    <span class="od-chip-label">편성주차</span>
                    <strong class="od-mono">{meta.planWeek || "-"}</strong>
                </div>
                <div class="od-chip od-chip-accent">
                    <span class="od-chip-label">조회열</span>
                    <strong>{meta.periodLabel || "-"}</strong>
                </div>
                {#each meta.filters || [] as f}
                    <div class="od-chip">
                        <span class="od-chip-label">{f.label}</span>
                        <strong>{f.value}</strong>
                    </div>
                {/each}
            </div>

            <!-- 본문 -->
            <div class="od-body">
                {#if isLoading}
                    <div class="od-state">
                        <div class="od-spinner"></div>
                        <p>수주 상세를 조회하고 있습니다...</p>
                    </div>
                {:else if errorMsg}
                    <div class="od-state od-state-error">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <p>{errorMsg}</p>
                        <button type="button" class="od-btn od-btn-retry" on:click={() => load(params)}>다시 조회</button>
                    </div>
                {:else if rows.length === 0}
                    <div class="od-state">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <p>조회된 수주 내역이 없습니다.</p>
                    </div>
                {:else}
                    <div class="od-table-wrap">
                        <table class="od-table">
                            <thead>
                                <tr>
                                    {#each COLUMNS as col}
                                        <th style="min-width: {col.width}px;">{col.label}</th>
                                    {/each}
                                </tr>
                            </thead>
                            <tbody>
                                {#each rows as row}
                                    <tr>
                                        {#each COLUMNS as col}
                                            <td
                                                class="od-{col.align}"
                                                class:od-mono={col.mono}
                                                class:od-num={col.num}
                                                class:od-agg={col.agg}
                                                class:od-wrap={col.wrap}
                                                class:od-zero={col.num && !Number(row[col.key])}
                                            >
                                                {#if col.badge}
                                                    <span class="od-badge" class:od-badge-pre={String(row[col.key] || "").includes("선생산")}>
                                                        {formatCell(col, row)}
                                                    </span>
                                                {:else}
                                                    {formatCell(col, row)}
                                                {/if}
                                            </td>
                                        {/each}
                                    </tr>
                                {/each}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colspan="5" class="od-center">합계</td>
                                    <td class="od-right od-num">{totals.so_qty.toLocaleString()}</td>
                                    <td class="od-right od-num">{totals.gi_qty.toLocaleString()}</td>
                                    <td class="od-right od-num">{totals.bill_qty.toLocaleString()}</td>
                                    <td></td>
                                    <td></td>
                                    <td class="od-right od-num od-agg">{totals.cell_qty.toLocaleString()}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                {/if}
            </div>

            <!-- 푸터 -->
            <div class="od-footer">
                <span class="od-note">※ 집계수량 : 선택한 셀에 집계된 수량 (과거 주차 = 그 주 출하수량, 현재·미래 주차 = 수주잔량)</span>
                <button type="button" class="od-btn od-btn-secondary" on:click={handleClose}>닫기</button>
            </div>
        </div>
    </div>
{/if}

<style>
    /* 뒷배경을 가리지 않는 레이어: 창 자신만 클릭을 받고, 나머지 화면은 그대로 조작 가능 */
    .od-layer {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 2100;
    }
    .od-box {
        position: absolute;
        pointer-events: auto;
        background: #ffffff;
        width: min(1280px, calc(100vw - 16px));
        border-radius: 8px;
        box-shadow: 0 12px 40px rgba(15, 23, 42, 0.35), 0 2px 8px rgba(15, 23, 42, 0.15);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #94a3b8;
    }
    .od-box.is-dragging {
        box-shadow: 0 18px 50px rgba(15, 23, 42, 0.45), 0 2px 8px rgba(15, 23, 42, 0.2);
    }

    /* 헤더 (이동 손잡이) */
    .od-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 18px;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
        flex-shrink: 0;
        cursor: grab;
        user-select: none;
        touch-action: none;
    }
    .od-box.is-dragging .od-header {
        cursor: grabbing;
    }
    .od-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #1e293b;
    }
    .od-title h3 {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 800;
    }
    .od-count {
        font-size: 0.72rem;
        background: #e2e8f0;
        color: #475569;
        padding: 2px 8px;
        border-radius: 12px;
        font-weight: 700;
    }
    .od-close {
        background: none;
        border: none;
        font-size: 1.15rem;
        color: #94a3b8;
        cursor: pointer;
        padding: 2px 6px;
        line-height: 1;
        transition: color 0.15s;
    }
    .od-close:hover {
        color: #0f172a;
    }

    /* 조회 조건 요약 칩 */
    .od-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 10px 18px;
        border-bottom: 1px solid #e2e8f0;
        background: #ffffff;
        flex-shrink: 0;
    }
    .od-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        font-size: 0.76rem;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        color: #334155;
    }
    .od-chip-label {
        color: #64748b;
        font-weight: 600;
    }
    .od-chip strong {
        font-weight: 800;
    }
    .od-chip-primary {
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #1d4ed8;
    }
    .od-chip-primary .od-chip-label {
        color: #60a5fa;
    }
    .od-chip-accent {
        background: #fffbeb;
        border-color: #fde68a;
        color: #92400e;
    }
    .od-chip-accent .od-chip-label {
        color: #d97706;
    }

    /* 본문 */
    .od-body {
        flex: 1;
        min-height: 160px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: #ffffff;
    }
    .od-table-wrap {
        flex: 1;
        overflow: auto;
    }
    .od-table-wrap::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    .od-table-wrap::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
    }
    .od-table-wrap::-webkit-scrollbar-track {
        background: #f8fafc;
    }

    .od-table {
        width: 100%;
        min-width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 0.82rem;
        color: #1e293b;
    }
    .od-table th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: #f1f5f9;
        color: #334155;
        font-weight: 700;
        font-size: 0.78rem;
        padding: 8px 8px;
        height: 34px;
        box-sizing: border-box;
        white-space: nowrap;
        border-bottom: 1px solid #cbd5e1;
        border-right: 1px solid #e2e8f0;
    }
    .od-table th:last-child {
        border-right: none;
    }
    .od-table td {
        padding: 7px 8px;
        border-bottom: 1px solid #e2e8f0;
        border-right: 1px solid #f1f5f9;
        white-space: nowrap;
        max-width: 280px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .od-table td:last-child {
        border-right: none;
    }
    .od-table tbody tr:hover td {
        background: #f8fafc;
    }
    /* 수요처처럼 긴 텍스트 컬럼: 말줄임 대신 줄바꿈으로 전체 표시 */
    .od-table td.od-wrap {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
        max-width: none;
        overflow-wrap: anywhere;
        line-height: 1.35;
    }
    .od-center {
        text-align: center;
    }
    .od-left {
        text-align: left;
    }
    .od-right {
        text-align: right;
    }
    .od-mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }
    .od-num {
        font-variant-numeric: tabular-nums;
        font-weight: 700;
    }
    .od-zero {
        color: #cbd5e1;
        font-weight: 600;
    }
    /* 집계수량 컬럼 (더블클릭한 셀 수량에 반영되는 값) 강조 */
    .od-table td.od-agg,
    .od-table th:last-child {
        border-left: 2px solid #c7d2fe;
    }
    .od-table tbody td.od-agg {
        background: #eef2ff;
        color: #3730a3;
    }
    .od-table tbody tr:hover td.od-agg {
        background: #e0e7ff;
    }
    .od-badge {
        display: inline-block;
        padding: 1px 8px;
        font-size: 0.72rem;
        font-weight: 700;
        border-radius: 10px;
        background: #eff6ff;
        color: #1d4ed8;
        border: 1px solid #bfdbfe;
    }
    .od-badge-pre {
        background: #fdf4ff;
        color: #86198f;
        border-color: #f5d0fe;
    }

    /* 합계 행 */
    .od-table tfoot td {
        position: sticky;
        bottom: 0;
        z-index: 2;
        background: #eef2ff;
        color: #3730a3;
        font-weight: 800;
        border-top: 2px solid #c7d2fe;
        border-bottom: none;
    }

    /* 로딩 / 빈 상태 / 오류 */
    .od-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 40px 20px;
        color: #94a3b8;
    }
    .od-state p {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 600;
    }
    .od-state-error {
        color: #dc2626;
    }
    .od-spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #e2e8f0;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: od-spin 0.8s linear infinite;
    }
    @keyframes od-spin {
        to {
            transform: rotate(360deg);
        }
    }

    /* 푸터 */
    .od-footer {
        padding: 10px 18px;
        border-top: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        background: #f8fafc;
        flex-shrink: 0;
    }
    .od-note {
        font-size: 0.72rem;
        color: #94a3b8;
    }
    .od-btn {
        padding: 7px 18px;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        flex-shrink: 0;
    }
    .od-btn-secondary {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        color: #475569;
    }
    .od-btn-secondary:hover {
        background: #f1f5f9;
    }
    .od-btn-retry {
        padding: 5px 14px;
        font-size: 0.8rem;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        color: #1d4ed8;
    }
    .od-btn-retry:hover {
        background: #dbeafe;
    }
</style>
